import type { FC } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import Sentry from "@similarweb/sentry-logger";

import type { TimeGranularitySymbol } from "app/common/utils/timeGranularity";
import { isTimeGranularitySymbol } from "app/common/utils/timeGranularity";
import ColorStack from "app/components/colorsStack/ColorStack";
import type { ILegendItems } from "app/components/React/Legends/legendsTypes";
import { CHART_COLORS } from "app/constants/ChartColors";
import { useStateWithDeeplink } from "app/custom-hooks/useStateWithDeeplink";
import { translate } from "app/filters/i18nFilter";
import RegionalsToggleBanner from "app/pages/industry-analysis/players/market-shares/RegionalsToggleBanner";
import { LoadingSpinner } from "app/pages/keyword-analysis/TotalTrafficPage/StyledComponents";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import { FixedBoxTitle } from "pro-features/components/BoxTitle/src/FixedBoxTitle";
import { EChartDisplayType } from "pro-features/components/Chart/src/configs/column-chart/columnChartConfig";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { getSelectedCategory } from "../../common/getSelectedCategory";
import { NoData } from "../../frame/NoData";
import { getPlayersQueryParams } from "../table/fetchData";

import { MarketSharesActionButtons } from "./MarketSharesActionButtons";
import { MarketSharesChartContainer } from "./MarketSharesChartContainer";
import { Header, SizeAndGrowthContent, MarketSharesWrapper, LoaderContainer, NoDataContainer } from "./StyledComponent";
import type { SwitchGraphCompareData } from "./types";
import { EChartType } from "./types";
import { getMarketSharesQueryParams } from "./utils/utils";

const { ABSOLUTE, PERCENTAGE } = EChartDisplayType;
const { AVERAGE, OVERTIME } = EChartType;

const ENDPOINT = "api/Market/Trends/SwitchGraphV2";

interface IMarketSharesProps {
    isDaily: boolean;
}

const MarketShares: FC<IMarketSharesProps> = ({ isDaily }: IMarketSharesProps) => {
    const { params } = useCurrentStateAndParams();
    const { duration, comparedDuration, webSource, category, marketSharesTimeGranularity } = params;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [responseData, setResponseData] = useState<SwitchGraphCompareData | null>(null);
    const isPeriodOverPeriod = DurationService.isPeriodOverPeriod();
    const includeRegionalDomains = params.includeRegionalDomains === "true";
    const [chartDisplayType, setChartDisplayType] = useState<EChartDisplayType>(() => {
        const { marketSharesDisplayType } = proNavigator.getParams();
        return marketSharesDisplayType === EChartDisplayType[ABSOLUTE].toString() ? ABSOLUTE : PERCENTAGE;
    });
    const chartRef = useRef<HTMLDivElement>(null);

    const [dashboardKeys, setDashboardKeys] = useState<ILegendItems>([]);

    const defaultGranularity: TimeGranularitySymbol = isDaily ? "D" : "M";
    const marketSharesTimeGranularityParam =
        typeof marketSharesTimeGranularity === "string" ? marketSharesTimeGranularity : undefined;
    const timeGranularity = isTimeGranularitySymbol(marketSharesTimeGranularityParam)
        ? marketSharesTimeGranularityParam
        : defaultGranularity;

    const [isMonthToDate, setIsMonthToDate] = useState(false);

    const selectedCategory = getSelectedCategory();

    useEffect(() => {
        setIsLoading(true);
        const fetchService = DefaultFetchService.getInstance();
        const queryParams = getMarketSharesQueryParams(timeGranularity, isMonthToDate);
        const controller = new AbortController();
        const rawDataPromise = fetchService.get<SwitchGraphCompareData>(ENDPOINT, queryParams, {
            cancellation: controller.signal,
        });

        rawDataPromise
            .then((result) => setResponseData(result))
            .catch((error) => {
                if (error.name !== "AbortError") {
                    Sentry.error("Error fetching market shares data -", error);
                }
            })
            .finally(() => setIsLoading(false));

        return () => {
            controller.abort();
        };
    }, [
        selectedCategory.id,
        selectedCategory.categoryHash,
        params.includeRegionalDomains,
        timeGranularity,
        isMonthToDate,
    ]);

    const [chartType, setChartType] = useStateWithDeeplink<EChartType>({
        param: "marketSharesChartType",
        stateToUrlAdapter: (chartType) => EChartType[chartType],
        urlToStateAdapter: (chartTypeParam) =>
            chartTypeParam === EChartType[OVERTIME].toString() && !isPeriodOverPeriod ? OVERTIME : AVERAGE,
    });
    const playersQueryParams = useMemo(() => getPlayersQueryParams(), []);
    const [legendItems, setLegendItems] = useState<ILegendItems>([]);
    const previousLegendItems = useRef<ILegendItems>([]);
    const [previousLegendItemsForRender, setPreviousLegendItemsForRender] = useState<ILegendItems>([]);
    const colorStack = useRef(new ColorStack(CHART_COLORS.chartMainColorsWithoutOthers));

    useEffect(() => {
        setPreviousLegendItemsForRender(previousLegendItems.current);
    }, [legendItems]);

    const handleLegendsChange = (changedLegendItems: ILegendItems) => {
        setLegendItems(changedLegendItems);
        previousLegendItems.current = changedLegendItems;
        setDashboardKeys(changedLegendItems);
    };

    const handleBeforeLegendItemsCreate = () => {
        colorStack.current.reset();
    };

    const onSwitcherClick = (selectionIndex) => {
        setChartDisplayType(selectionIndex);
        allTrackers.dispatchTrackEvent("toggle", "click", "absolute-share toggle", {
            selectedView: EChartDisplayType[selectionIndex],
            chartViewType: EChartType[chartType],
        });

        const params = {
            marketSharesDisplayType: EChartDisplayType[selectionIndex],
        };

        proNavigator.applyUpdateParamsAsync(params, {
            reload: false,
        });
    };

    const handleTimeGranularityChange = (granularity: TimeGranularitySymbol) => {
        void proNavigator.applyUpdateParamsAsync({ marketSharesTimeGranularity: granularity }, { reload: false });
    };

    const handleIncludeRegionalDomainsToggle = (isRegionalsEnabled: boolean) => {
        const newRegionalDomainsState = isRegionalsEnabled;
        allTrackers.dispatchTrackEvent("toggle", "click", "regional_domains_toggle", {
            regionalDomainsState: newRegionalDomainsState ? "on" : "off",
        });
        void proNavigator.updateParams({ includeRegionalDomains: newRegionalDomainsState });
    };

    const { from, to, compareFrom, compareTo } = useMemo(
        () => DurationService.getDurationData(duration, comparedDuration).forAPI,
        [duration, comparedDuration],
    );

    const subtitleFilters = [
        {
            filter: "date",
            value: {
                from,
                to,
                compareFrom,
                compareTo,
            },
        },
    ];

    return (
        <MarketSharesWrapper data-automation="MarketShares">
            <Header>
                <FlexColumn gap={"8px"}>
                    <FixedBoxTitle
                        title={translate("market.players.market.shares.header.title")}
                        tooltip={translate("market.players.market.shares.header.title.tooltip")}
                    />
                    <BoxSubtitle filters={subtitleFilters} />
                </FlexColumn>
                <MarketSharesActionButtons
                    chartRef={chartRef}
                    chartType={chartType}
                    chartDisplayType={chartDisplayType}
                    dashboardKeys={dashboardKeys}
                    isPeriodOverPeriod={isPeriodOverPeriod}
                    includeRegionalDomains={includeRegionalDomains}
                    onSwitcherClick={onSwitcherClick}
                    setChartType={setChartType}
                    webSource={webSource}
                    category={category}
                    playersQueryParams={playersQueryParams}
                    timeGranularity={timeGranularity}
                    onTimeGranularityChange={handleTimeGranularityChange}
                    isDaily={isDaily}
                    isMonthToDate={isMonthToDate}
                    onMonthToDateToggle={() => setIsMonthToDate((prevMTD) => !prevMTD)}
                />
            </Header>
            <RegionalsToggleBanner
                isDisabled={false}
                isRegionalsEnabled={includeRegionalDomains}
                toggleRegionals={handleIncludeRegionalDomainsToggle}
            />
            <SizeAndGrowthContent>
                {isLoading ? (
                    <LoaderContainer>
                        <LoadingSpinner />
                    </LoaderContainer>
                ) : responseData === null ? (
                    <NoDataContainer>
                        <NoData
                            noDataTitleKey="global.nodata.notavilable"
                            noDataSubTitleKey="workspaces.marketing.nodata.subtitle"
                        />
                    </NoDataContainer>
                ) : (
                    <MarketSharesChartContainer
                        chartRef={chartRef}
                        chartType={chartType}
                        chartDisplayType={chartDisplayType}
                        rawData={responseData}
                        setDashboardKeys={setDashboardKeys}
                        legendItems={legendItems}
                        setLegendItems={setLegendItems}
                        previousLegendItems={previousLegendItemsForRender}
                        colorStack={colorStack}
                        timeGranularity={timeGranularity}
                        onLegendsChange={handleLegendsChange}
                        onBeforeLegendItemsCreate={handleBeforeLegendItemsCreate}
                        isMonthToDate={isMonthToDate}
                        isDaily={isDaily}
                    />
                )}
            </SizeAndGrowthContent>
        </MarketSharesWrapper>
    );
};

export default MarketShares;
